/**
 * L'installatore iscritto da Datacom, e l'accesso con un codice via email
 * (06/10/2026). Regole senza API di Deno: le usano `crea-installatore` e
 * `accesso-codice`, e si provano da vitest (src/lib/installatore.test.ts).
 *
 * PERCHE' (Umberto e Davide, 06/10)
 * Agli eventi in sede (Ajax Experience il 22/10, corso IRAI a novembre)
 * l'installatore e' li', davanti: un amministratore gli crea l'account dal
 * pannello, a lui arriva una mail con un codice, entra con email + codice e
 * dentro l'app sceglie la sua password. Nessuno di Datacom la conosce.
 *
 * Il segno «password da impostare» sta nei metadati dell'utente: non e' una
 * protezione (l'utente lo puo' cambiare da se'), e' solo il cartello che
 * porta alla pagina giusta al primo accesso.
 */
import { GRATUITO_DOMANDE_SETTIMANA } from "./offerta.ts";

export const SEGNO_PASSWORD_DA_IMPOSTARE = "password_da_impostare";

/**
 * MARCHI E DURATA GIA' ALLA NASCITA (09/10/2026).
 *
 * Umberto: «creare account a mio piacimento: due che funzionano con INIM, due
 * con Ajax». Davide: si parte dall'email della persona; Umberto fa quasi
 * tutto, lei entra e scrive solo gli ultimi dati. Fino a qui l'account nasceva
 * in prova e i marchi si davano dopo, da «Installatori»: due passaggi, e nel
 * mezzo l'account vedeva tutto per 7 giorni. Ora marchi e scadenza si scelgono
 * nel modulo e l'account nasce gia' attivo su quelli. Dal 13/10 nessun marchio
 * scelto = tutti i marchi, per la durata scelta (la prova «a 100 domande» resta
 * a chi si iscrive da solo).
 *
 * Gli stessi quattro del vincolo `profiles_brands_valid` (e di MARCHI_VALIDI in
 * account_prova.ts: un test controlla che restino uguali). Qui non si importa
 * _shared/marchi.ts perche' legge l'ambiente di Deno e questo file lo usa anche
 * l'app.
 */
export const MARCHI_ISCRIZIONE = ["INIM", "AJAX", "DAITEM", "NOTIFIER"] as const;

/**
 * LE DURATE, IN GIORNI E IN MESI (13/10/2026).
 *
 * Davide: l'admin deve poter scegliere quanto dura un account, sempre. Fino al
 * 12/10 la durata c'era solo in mesi e solo con dei marchi; i funzionari dei
 * costruttori stavano in un'altra scheda, con i giorni. Ora una lista sola, che
 * vale anche per prolungare un account esistente («+15 giorni», «+3 mesi»).
 * `null` = senza scadenza.
 */
export const DURATE = [
  { chiave: "7g", giorni: 7, nome: "7 giorni" },
  { chiave: "15g", giorni: 15, nome: "15 giorni" },
  { chiave: "1m", mesi: 1, nome: "1 mese" },
  { chiave: "3m", mesi: 3, nome: "3 mesi" },
  { chiave: "6m", mesi: 6, nome: "6 mesi" },
  { chiave: "12m", mesi: 12, nome: "1 anno" },
  { chiave: "24m", mesi: 24, nome: "2 anni" },
] as const;
export type Durata = (typeof DURATE)[number];
export type ChiaveDurata = Durata["chiave"];
// 3 mesi di partenza (Davide, 26/10/2026): sono i 3 mesi promessi al telefono ai clienti chiamati
// dalla campagna (incontro del 22/10). Con «1 anno» gia' scelto, chi crea gli account di corsa
// avrebbe regalato un anno. Le altre durate restano a un clic.
export const DURATA_PREDEFINITA: ChiaveDurata = "3m";

/** Il vecchio modulo (09-12/10) mandava `mesi`: resta accettato. */
export const DURATE_MESI = [1, 3, 6, 12, 24] as const;

export function durataDa(chiave: unknown): Durata | null {
  return DURATE.find((d) => d.chiave === chiave) ?? null;
}

export interface NuovoInstallatore {
  email: string;
  /** Vuoti se l'admin non li sa: li scrive la persona al primo accesso. */
  nome: string;
  cognome: string;
  azienda: string;
  partita_iva: string | null;
  telefono: string | null;
  citta: string | null;
  provincia: string | null;
  /**
   * Dal 13/10 mai vuoto per chi usa il modulo nuovo (nessun marchio scelto =
   * tutti). Vuoto solo dal vecchio modulo: la prova di 7 giorni di sempre.
   */
  marchi: string[];
  /** Per quanto vale, null = senza scadenza. Conta solo se ci sono marchi. */
  durata: ChiaveDurata | null;
  /**
   * Funzionario di un fornitore o costruttore, non un installatore (13/10/2026).
   * Davide, sui dieci di INIM, Ajax, DAITEM e Notifier che Umberto vuole far
   * provare: «se creiamo l'account noi può anche bypassare la p.iva», e solo
   * per i fornitori. Nel profilo diventa `account_prova = true`, il segno degli
   * account di prova dei costruttori (25/09): niente partita IVA, niente dati da
   * completare, fuori da coda, comunicazioni di massa e statistiche.
   */
  fornitore: boolean;
}

export function emailValida(s: unknown): s is string {
  return typeof s === "string" && s.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());
}

export function normalizzaEmail(s: string): string {
  return s.trim().toLowerCase();
}

/** Datacom e B2Brand: entrano con tutti i marchi e senza partita IVA (trigger del 01/09). */
export function emailInterna(email: string): boolean {
  return email.endsWith("@datacomtecnologie.it") || email.endsWith("@b2brand.it");
}

/** Copia di src/lib/partitaIva.ts (le funzioni non vedono src/): un test controlla che restino uguali. */
export function normalizzaPartitaIva(grezza: string): string {
  return (grezza ?? "").toUpperCase().replace(/[^0-9]/g, "");
}

export function partitaIvaFormalmenteValida(grezza: string): boolean {
  const p = normalizzaPartitaIva(grezza);
  if (p.length !== 11) return false;
  if (/^0+$/.test(p)) return false;
  let somma = 0;
  for (let i = 0; i < 10; i++) {
    const c = p.charCodeAt(i) - 48;
    if (i % 2 === 0) {
      somma += c;
    } else {
      const d = c * 2;
      somma += d > 9 ? d - 9 : d;
    }
  }
  const controllo = (10 - (somma % 10)) % 10;
  return controllo === p.charCodeAt(10) - 48;
}

function campo(c: Record<string, unknown>, nome: string, max: number): string {
  return String(c[nome] ?? "").trim().replace(/\s+/g, " ").slice(0, max);
}

/**
 * Controlla e ripulisce quello che arriva dal pannello.
 *
 * Dal 09/10 serve solo l'email: nome, cognome, impresa, partita IVA e telefono
 * li scrive l'admin se li sa, altrimenti li chiede l'app al primo accesso
 * (`campiDaCompletare` in src/lib/iscrizione.ts). Quello che c'e' si controlla
 * come prima: una partita IVA scritta deve tornare.
 */
export function leggiNuovoInstallatore(corpo: unknown): { errore: string } | { installatore: NuovoInstallatore } {
  const c = (corpo ?? {}) as Record<string, unknown>;
  if (!emailValida(c.email)) return { errore: "Email non valida" };
  const email = normalizzaEmail(c.email);
  const pivaScritta = campo(c, "partita_iva", 20);
  let partita_iva: string | null = null;
  if (pivaScritta) {
    if (!partitaIvaFormalmenteValida(pivaScritta)) return { errore: "Partita IVA non valida: controlla il numero" };
    partita_iva = normalizzaPartitaIva(pivaScritta);
  }
  const telefono = campo(c, "telefono", 30) || null;
  if (telefono && !/^[0-9 +()/.-]+$/.test(telefono)) return { errore: "Telefono: solo numeri, spazi e + ( ) -" };

  const grezzi = Array.isArray(c.marchi) ? c.marchi : c.marchi == null ? [] : null;
  if (grezzi === null) return { errore: "Marchi non leggibili" };
  let marchi = [...new Set(grezzi.map((m) => String(m).trim().toUpperCase()))];
  const estranei = marchi.filter((m) => !(MARCHI_ISCRIZIONE as readonly string[]).includes(m));
  if (estranei.length) return { errore: `Marchio sconosciuto: ${estranei.join(", ")}` };
  // L'ordine e' sempre quello dell'elenco, qualunque sia l'ordine dei clic.
  marchi.sort((a, b) => MARCHI_ISCRIZIONE.indexOf(a as never) - MARCHI_ISCRIZIONE.indexOf(b as never));

  let durata: ChiaveDurata | null = null;
  if ("durata" in c) {
    // Il modulo del 13/10: la durata vale sempre, e nessun marchio = tutti.
    if (c.durata !== null) {
      const d = durataDa(c.durata);
      if (!d) return { errore: "Durata non valida" };
      durata = d.chiave;
    }
    if (!marchi.length) marchi = [...MARCHI_ISCRIZIONE];
  } else if (marchi.length) {
    // Il modulo del 09/10, magari ancora aperto in un browser: `mesi`, e
    // nessun marchio = la prova di 7 giorni.
    if (c.mesi === undefined) durata = DURATA_PREDEFINITA;
    else if (c.mesi === null) durata = null;
    else if ((DURATE_MESI as readonly number[]).includes(Number(c.mesi))) durata = `${Number(c.mesi)}m` as ChiaveDurata;
    else return { errore: "Durata non valida" };
  }

  return {
    installatore: {
      email,
      nome: campo(c, "nome", 80),
      cognome: campo(c, "cognome", 80),
      azienda: campo(c, "azienda", 120),
      partita_iva,
      telefono,
      citta: campo(c, "citta", 80) || null,
      provincia: campo(c, "provincia", 80) || null,
      marchi,
      durata,
      fornitore: c.fornitore === true,
    },
  };
}

/**
 * La scadenza a `mesi` mesi da oggi, a fine giornata (come «Installatori»,
 * che salva `YYYY-MM-DDT23:59:59Z`). Il 31 gennaio + 1 mese e' il 28 o 29
 * febbraio, non il 3 marzo.
 */
export function scadenzaFraMesi(mesi: number, adesso: Date = new Date()): string {
  const anno = adesso.getUTCFullYear();
  const mese = adesso.getUTCMonth() + mesi;
  const ultimoDelMese = new Date(Date.UTC(anno, mese + 1, 0)).getUTCDate();
  return new Date(Date.UTC(anno, mese, Math.min(adesso.getUTCDate(), ultimoDelMese), 23, 59, 59)).toISOString();
}

/**
 * La scadenza dopo `durata`, partendo da `da`, a fine giornata. Serve alla
 * nascita (da oggi) e al prolungamento (da `basePerProlungare`).
 */
export function scadenzaDopo(durata: ChiaveDurata, da: Date = new Date()): string {
  const d = durataDa(durata)!;
  if ("mesi" in d) return scadenzaFraMesi(d.mesi, da);
  const giorno = new Date(Date.UTC(da.getUTCFullYear(), da.getUTCMonth(), da.getUTCDate() + d.giorni, 23, 59, 59));
  return giorno.toISOString();
}

/**
 * Da dove si conta un prolungamento (13/10): dalla scadenza se e' ancora
 * davanti (chi rinnova prima non perde i giorni che gli restano), da oggi se
 * e' gia' passata o se l'account non ne aveva una.
 */
export function basePerProlungare(scadenza: string | null, adesso: Date = new Date()): Date {
  const s = scadenza ? new Date(scadenza) : null;
  return s && s.getTime() > adesso.getTime() ? s : adesso;
}

/**
 * Cosa scrivere nel profilo appena nato: i marchi e fin quando valgono.
 * null = nessun marchio (solo dal vecchio modulo): l'account parte con la
 * prova di 7 giorni. Lo scrive il server con la chiave di servizio (`brands` e
 * `scadenza` sono colonne riservate: l'utente non le puo' toccare).
 */
export function abbonamentoIniziale(
  i: Pick<NuovoInstallatore, "marchi" | "durata">,
  adesso: Date = new Date(),
): { brands: string[]; scadenza: string | null } | null {
  if (!i.marchi.length) return null;
  return { brands: [...i.marchi], scadenza: i.durata === null ? null : scadenzaDopo(i.durata, adesso) };
}

/** «9 ottobre 2027»: la data della scadenza come si legge in una mail. */
export function dataScadenzaLeggibile(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

/** «INIM», «INIM e AJAX», «INIM, AJAX e DAITEM» (anche «nome e telefono»). */
export function elencoLeggibile(voci: readonly string[]): string {
  if (voci.length <= 1) return voci.join("");
  return `${voci.slice(0, -1).join(", ")} e ${voci[voci.length - 1]}`;
}

/**
 * Quello che l'admin non ha scritto e che la persona completera' al primo
 * accesso. Al fornitore l'app non chiede niente (`campiDaCompletare` salta gli
 * account di prova).
 */
export function datiMancanti(i: Pick<NuovoInstallatore, "email" | "nome" | "cognome" | "azienda" | "partita_iva" | "telefono" | "fornitore">): string[] {
  if (i.fornitore) return [];
  const interna = emailInterna(i.email);
  const mancanti: string[] = [];
  if (!i.nome) mancanti.push("nome");
  if (!i.cognome) mancanti.push("cognome");
  if (!interna) {
    if (!i.azienda) mancanti.push("impresa");
    if (!i.partita_iva) mancanti.push("partita IVA");
    if (!i.telefono) mancanti.push("telefono");
  }
  return mancanti;
}

/** I metadati con cui nasce l'utente: il trigger handle_new_user ne ricava il profilo. */
export function metadatiNuovoInstallatore(i: NuovoInstallatore, creatoDa: string): Record<string, unknown> {
  return {
    first_name: i.nome,
    last_name: i.cognome,
    company: i.azienda,
    partita_iva: i.partita_iva ?? "",
    telefono: i.telefono ?? "",
    city: i.citta ?? "",
    province: i.provincia ?? "",
    [SEGNO_PASSWORD_DA_IMPOSTARE]: true,
    iscritto_da: creatoDa,
  };
}

/**
 * L'utente con QUESTA email, fra quelli che restituisce la ricerca di
 * Supabase. La ricerca e' «contiene» (LIKE '%…%', e «_» vale come jolly):
 * va sempre ristretta all'uguaglianza esatta, altrimenti mario@x.it
 * troverebbe anche anna.mario@x.it.
 */
export function utenteConEmail<T extends { email?: string | null }>(utenti: T[], email: string): T | null {
  const cercata = normalizzaEmail(email);
  return utenti.find((u) => (u.email ?? "").toLowerCase() === cercata) ?? null;
}

export function passwordDaImpostare(metadati: Record<string, unknown> | null | undefined): boolean {
  return metadati?.[SEGNO_PASSWORD_DA_IMPOSTARE] === true;
}

/**
 * LA PASSWORD PROVVISORIA (14/10/2026). Ai funzionari di Atral e Honeywell
 * la mail col codice la fermava il filtro dell'azienda (Brevo: «consegnata»,
 * poi clic automatici entro un minuto e nessuno la trova). L'admin crea una
 * password provvisoria, la manda su WhatsApp, la persona entra con email e
 * password e sceglie subito la sua: il segno «password da impostare» resta
 * acceso e ProtectedRoute la porta a /imposta-password, come col codice.
 * Questo secondo segno dice solo alla pagina d'accesso di chiedere la
 * password invece del codice; non protegge niente (l'utente puo' cambiarlo).
 */
export const SEGNO_PASSWORD_PROVVISORIA = "password_provvisoria";

export function passwordProvvisoria(metadati: Record<string, unknown> | null | undefined): boolean {
  return metadati?.[SEGNO_PASSWORD_PROVVISORIA] === true;
}

/** La pagina d'accesso chiede il CODICE (account nuovo senza password) o la password, anche provvisoria. */
export function chiedeIlCodice(metadati: Record<string, unknown> | null | undefined): boolean {
  return passwordDaImpostare(metadati) && !passwordProvvisoria(metadati);
}

const SITO = "https://ai.datacomtecnologie.it";
const INDIRIZZO_ACCESSO = `${SITO}/login`;
const INDIRIZZO_BENVENUTO = `${SITO}/benvenuto`;

/**
 * Gli store: gli stessi di src/lib/scaricaApp.ts (un test li confronta).
 * La mail non sa che telefono ha chi legge: i due link stanno sotto il tasto,
 * che invece porta a /benvenuto, dove la pagina lo riconosce.
 */
export const APP_STORE_MAIL = "https://apps.apple.com/it/app/datacom-ai/id6809735960";
export const GOOGLE_PLAY_MAIL = "https://play.google.com/store/apps/details?id=it.datacomtecnologie.ai";
/**
 * Il QR per chi legge la mail dal computer: un'immagine fissa (le mail non
 * mostrano gli SVG e non si puo' disegnare un QR per ogni mail) verso
 * /app?c=invito, che riconosce il telefono e salta «Crea l'account».
 * Generata con scripts/firma/genera_qr.mjs, pubblicata in public/mail/.
 */
export const QR_MAIL = `${SITO}/mail/qr-invito.png`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}

/**
 * Il link «Entra dal sito»: apre la pagina di accesso gia' al passo del codice,
 * con email e codice scritti. I dati stanno dopo il «#»: il browser non li manda
 * al server (niente email nei registri di nginx), e la pagina li toglie
 * dall'indirizzo appena li ha letti. Entrare resta un clic dell'installatore
 * su «Entra»: i filtri antivirus della posta che aprono i link da soli non
 * consumano il codice.
 */
export function linkAccesso(email: string, codice: string, benvenuto: boolean): string {
  const dati = new URLSearchParams({ email, codice });
  if (benvenuto) dati.set("benvenuto", "1");
  return `${INDIRIZZO_ACCESSO}#${dati.toString()}`;
}

/**
 * Il tasto della mail di benvenuto (23/10/2026, strade A e B scelte da Davide):
 * porta a /benvenuto, che riconosce il telefono, mostra lo store giusto e il
 * codice col tasto «Copia», per fare il primo accesso DALL'APP. Stessi dati
 * dopo il «#», per lo stesso motivo di linkAccesso.
 */
export function linkBenvenuto(email: string, codice: string): string {
  return `${INDIRIZZO_BENVENUTO}#${new URLSearchParams({ email, codice, benvenuto: "1" }).toString()}`;
}

/**
 * Il link da mandare su WhatsApp (09/10/2026): come quello della mail ma SENZA
 * il codice, che vale poco e non deve girare fuori dalla posta. Chi riceve il
 * link inoltrato non entra: il codice arriva solo alla casella della persona.
 *
 * DAL 02/11/2026 PORTA ALL'APP, come il tasto della mail (riunione in sede del
 * 02/11, telefonata ai clienti): /benvenuto riconosce il telefono e mostra lo
 * store giusto, con l'email scritta e «scrivi il codice della mail». Fino a
 * oggi apriva l'accesso dal SITO (/login), e chi arrivava da WhatsApp restava
 * nel browser senza mai scaricare l'app. Dentro l'app /benvenuto porta da se'
 * all'accesso col codice.
 */
export function linkInvito(email: string): string {
  return `${INDIRIZZO_BENVENUTO}#${new URLSearchParams({ email, benvenuto: "1" }).toString()}`;
}

/** Cosa contiene l'account appena creato, per la mail di benvenuto (09/10/2026). */
export interface ContenutoAccount {
  /** I marchi scelti dall'admin; vuoto = prova di 7 giorni. */
  marchi?: string[];
  /** ISO, o null = senza scadenza. */
  scadenza?: string | null;
  /** I dati che l'admin non ha scritto e che chiedera' l'app («partita IVA», «telefono»…). */
  mancanti?: string[];
}

/** La riga «cosa comprende» del benvenuto. Vuota se non ci sono marchi. */
export function rigaContenuto(c: ContenutoAccount): string {
  if (!c.marchi?.length) return "";
  const quali = `${c.marchi.length === 1 ? "il marchio" : "i marchi"} ${elencoLeggibile(c.marchi)}`;
  return c.scadenza
    ? `Il tuo account comprende ${quali}, fino al ${dataScadenzaLeggibile(c.scadenza)}.`
    : `Il tuo account comprende ${quali}.`;
}

/**
 * La riga «e alla scadenza?» del benvenuto (02/11/2026). Alla riunione in sede
 * Umberto ha chiesto che fosse chiaro a tutti: nessun rinnovo automatico,
 * nessun addebito. Vale per ogni account con scadenza (i 3 mesi della
 * telefonata come la licenza attivata da Datacom), quindi niente «regalo».
 * Gli avvisi li manda la funzione avvisi-scadenza. Vuota senza scadenza.
 */
export function rigaScadenza(c: ContenutoAccount): string {
  if (!c.marchi?.length || !c.scadenza) return "";
  return "Alla scadenza non ti addebitiamo niente: ti avvisiamo per email un mese prima e, se non rinnovi, " +
    `l'account resta gratuito, con gli strumenti di calcolo e ${GRATUITO_DOMANDE_SETTIMANA} domande a settimana sulle norme.`;
}

/** La riga «al primo accesso ti chiediamo…». Vuota se l'admin ha scritto tutto. */
export function rigaMancanti(c: ContenutoAccount): string {
  if (!c.mancanti?.length) return "";
  return `Al primo accesso ti chiediamo anche ${elencoLeggibile(c.mancanti)}: ci vuole un minuto.`;
}

/** La riga della mail quando il codice e' quello di pochi minuti prima (02/11/2026). */
export const RIGA_STESSO_CODICE = "È lo stesso codice che ti abbiamo mandato pochi minuti fa: puoi usare l'una o l'altra email.";

/** Il testo del banner «Scarica l'app» del sito (src/lib/bannerApp.ts): Davide, 23/10, lo vuole anche nella mail. */
export const TESTO_APP_MAIL = "Hai l'app Datacom AI? Aprila. Non ce l'hai? È gratis.";

/**
 * La mail col codice. `benvenuto` = l'account l'ha appena creato Datacom.
 *
 * DAL 23/10/2026 IL PRIMO ACCESSO SI FA DALL'APP (incontro del 22/10, strade A e
 * B scelte da Davide; pagina della scelta docs/design/dalla_mail_all_app_2026-10-23.html).
 * Prima la mail portava al SITO e l'installatore restava nel browser senza
 * sapere che c'e' l'app. Ora: (1) il tasto porta a /benvenuto, che mostra lo
 * store giusto per il suo telefono; (2) il codice si scrive nell'app (anche le
 * versioni sugli store dal 06/10 sanno entrare col codice); per chi legge dal
 * computer, un QR. Il sito resta come ripiego, in piccolo.
 *
 * La mail chiesta da «Entra con un codice» (benvenuto = false) non cambia: chi
 * la chiede e' gia' sulla pagina dove scriverlo.
 *
 * `stessoCodice` (02/11/2026): e' il codice gia' mandato pochi minuti fa
 * (stesso_codice.ts). Lo si dice, cosi' chi ha due mail sa che vanno bene tutte e due.
 */
export function mailCodice(
  codice: string,
  opz: { benvenuto: boolean; nome?: string; email?: string; stessoCodice?: boolean } & ContenutoAccount,
): {
  oggetto: string;
  html: string;
  testo: string;
} {
  if (opz.benvenuto) return mailBenvenuto(codice, opz);
  const saluto = opz.nome ? `Ciao ${opz.nome},` : "Ciao,";
  // Il codice NON va nell'oggetto: l'oggetto si salva nel registro delle mail
  // (invii_email), che il pannello mostra; un codice ancora valido li' dentro
  // farebbe entrare chiunque legga il registro.
  const oggetto = "Il tuo codice per entrare in Datacom AI";
  const righe = [
    "Per entrare in Datacom AI scrivi questo codice nella pagina di accesso, insieme alla tua email.",
    ...(opz.stessoCodice ? [RIGA_STESSO_CODICE] : []),
  ];
  const chiusura = "Il codice vale poco tempo: se non funziona più, chiedine uno nuovo dalla pagina di accesso. Se non l'hai chiesto tu, ignora questa email.";
  const link = opz.email ? linkAccesso(opz.email, codice, false) : INDIRIZZO_ACCESSO;
  const pulsante = "Apri la pagina di accesso";

  const html = guscioMail(oggetto, `
            <p style="${P}">${escapeHtml(saluto)}</p>
            ${righe.map((r) => `<p style="${P}">${escapeHtml(r)}</p>`).join("\n            ")}
            <p style="margin:8px 0 24px 0;${CODICE}">${escapeHtml(codice)}</p>
            ${tasto(link, pulsante)}
            <p style="margin:0 0 24px 0;font-size:14px;line-height:1.6;color:#6b7280;">Oppure apri <a href="${INDIRIZZO_ACCESSO}" style="color:#0B1220;font-weight:600;">${INDIRIZZO_ACCESSO.replace("https://", "")}</a></p>
            <p style="margin:0;font-size:13px;line-height:1.6;color:#6b7280;">${escapeHtml(chiusura)}</p>`);

  const testo = [
    saluto, "", ...righe, "", `Codice: ${codice}`, "", `${pulsante}: ${link}`, "",
    chiusura, "", "Datacom Tecnologie - Supporto tecnico installatori",
  ].join("\n");
  return { oggetto, html, testo };
}

const P = "margin:0 0 16px 0;font-size:15px;line-height:1.6;color:#374151;";
const ETICHETTA = "margin:24px 0 8px 0;font-size:12px;line-height:1.4;letter-spacing:0.12em;text-transform:uppercase;font-weight:700;color:#6b7280;";
const CODICE = "font-size:34px;line-height:1.2;font-weight:700;letter-spacing:0.18em;color:#0B1220;font-family:'SFMono-Regular',Menlo,Consolas,monospace;";

function tasto(link: string, etichetta: string): string {
  return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:0 0 12px 0;"><tr><td bgcolor="#0B1220" style="border-radius:8px;background:#0B1220;">
              <a href="${escapeHtml(link)}" style="display:inline-block;padding:14px 22px;font-size:16px;line-height:1.2;font-weight:600;color:#ffffff;text-decoration:none;border-radius:8px;">${escapeHtml(etichetta)}</a>
            </td></tr></table>`;
}

/** La cornice comune: tabelle e stili in linea, come vogliono i programmi di posta. */
function guscioMail(oggetto: string, corpo: string): string {
  return `<!DOCTYPE html>
<html lang="it">
  <head><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1" /><title>${escapeHtml(oggetto)}</title></head>
  <body style="margin:0;padding:0;background:#f4f6f8;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0B1220;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f4f6f8;padding:32px 16px;">
      <tr><td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:560px;background:#ffffff;border-radius:12px;border:1px solid #e5e7eb;overflow:hidden;">
          <tr><td style="padding:24px 28px;border-bottom:1px solid #eef0f3;font-size:14px;letter-spacing:0.08em;text-transform:uppercase;color:#6b7280;">Datacom AI</td></tr>
          <tr><td style="padding:28px;">${corpo}
          </td></tr>
          <tr><td style="padding:18px 28px;background:#f9fafb;border-top:1px solid #eef0f3;font-size:12px;color:#6b7280;">
            Datacom Tecnologie · Supporto tecnico installatori<br/>Questa email è stata inviata automaticamente, non rispondere.
          </td></tr>
        </table>
      </td></tr>
    </table>
  </body>
</html>`;
}

function mailBenvenuto(
  codice: string,
  opz: { nome?: string; email?: string } & ContenutoAccount,
): { oggetto: string; html: string; testo: string } {
  const saluto = opz.nome ? `Ciao ${opz.nome},` : "Ciao,";
  const oggetto = "Il tuo account Datacom AI è pronto";
  const apertura = [
    "Datacom Tecnologie ti ha creato l'account di Datacom AI, l'assistente tecnico per gli installatori.",
    rigaContenuto(opz),
    rigaScadenza(opz),
  ].filter(Boolean);
  const passo1 = "1 · Scarica l'app";
  const passo2 = "2 · Aprila, scrivi la tua email e questo codice";
  const dopoCodice = ["Subito dopo scegli la tua password: la conosci solo tu.", rigaMancanti(opz)].filter(Boolean);
  const pulsante = "Apri o scarica l'app";
  const linkTasto = opz.email ? linkBenvenuto(opz.email, codice) : INDIRIZZO_BENVENUTO;
  const linkSito = opz.email ? linkAccesso(opz.email, codice, true) : INDIRIZZO_ACCESSO;
  const qr = "Stai leggendo dal computer? Inquadra il codice col telefono: ti porta all'app giusta.";
  const ripiego = "Preferisci il browser? Entra dal sito";
  // 14/10: chi la apre giorni dopo trova il codice scaduto. Nell'app (e sul
  // sito) «Mandane uno nuovo» ne manda un altro alla stessa casella.
  const chiusura =
    "Il codice vale 24 ore. Se non funziona più, nell'app (o sul sito) premi «Mandane uno nuovo»: te ne mandiamo subito un altro. " +
    "Dal secondo accesso in poi entri con email e password, dall'app o dal sito. Se non sai di cosa si tratta, ignora questa email.";

  const html = guscioMail(oggetto, `
            <p style="${P}">${escapeHtml(saluto)}</p>
            ${apertura.map((r) => `<p style="${P}">${escapeHtml(r)}</p>`).join("\n            ")}
            <p style="${ETICHETTA}">${escapeHtml(passo1)}</p>
            <p style="${P}">${escapeHtml(TESTO_APP_MAIL)}</p>
            ${tasto(linkTasto, pulsante)}
            <p style="margin:0 0 24px 0;font-size:14px;line-height:1.6;color:#6b7280;"><a href="${APP_STORE_MAIL}" style="color:#0B1220;font-weight:600;">App Store</a> per iPhone · <a href="${GOOGLE_PLAY_MAIL}" style="color:#0B1220;font-weight:600;">Google Play</a> per Android</p>
            <p style="${ETICHETTA}">${escapeHtml(passo2)}</p>
            <p style="margin:0 0 12px 0;${CODICE}">${escapeHtml(codice)}</p>
            ${dopoCodice.map((r) => `<p style="${P}">${escapeHtml(r)}</p>`).join("\n            ")}
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin:8px 0 20px 0;border-top:1px solid #eef0f3;"><tr>
              <td width="96" style="padding:16px 16px 0 0;vertical-align:top;"><img src="${QR_MAIL}" width="88" height="88" alt="Codice QR per scaricare l'app Datacom AI" style="display:block;border:0;width:88px;height:88px;" /></td>
              <td style="padding:16px 0 0 0;vertical-align:middle;font-size:13px;line-height:1.5;color:#6b7280;">${escapeHtml(qr)}</td>
            </tr></table>
            <p style="margin:0 0 16px 0;font-size:13px;line-height:1.6;color:#6b7280;">Preferisci il browser? <a href="${escapeHtml(linkSito)}" style="color:#0B1220;font-weight:600;">Entra dal sito</a></p>
            <p style="margin:0;font-size:13px;line-height:1.6;color:#6b7280;">${escapeHtml(chiusura)}</p>`);

  const testo = [
    saluto, "", ...apertura, "",
    passo1.toUpperCase(), TESTO_APP_MAIL, `${pulsante}: ${linkTasto}`, `App Store (iPhone): ${APP_STORE_MAIL}`, `Google Play (Android): ${GOOGLE_PLAY_MAIL}`, "",
    passo2.toUpperCase(), `Codice: ${codice}`, ...dopoCodice, "",
    `${ripiego}: ${linkSito}`, "",
    chiusura, "", "Datacom Tecnologie - Supporto tecnico installatori",
  ].join("\n");
  return { oggetto, html, testo };
}

// ─── La partita IVA controllata all'iscrizione (06/10/2026) ────────────────
//
// Davide, 06/10: chi iscrive l'installatore deve vedere SUBITO se la partita
// IVA e' coerente (impresa vera, attiva, che mestiere fa), con lui davanti.
// Il pannello chiede a crea-installatore { azione: "controlla" }, che fa la
// prova a secco di verifica-piva (Registro Imprese + VIES) e restituisce
// l'esito FIRMATO. Alla creazione il pannello rimanda l'esito: se la firma
// torna, e' quello che ha calcolato il server da meno di un'ora per QUESTA
// partita IVA, e non si paga un secondo controllo (0,10 € l'uno, con un tetto
// settimanale di 100 per tutto il servizio: a un evento con 40 iscrizioni i
// controlli doppi si mangerebbero il tetto di chi si iscrive da solo).
// Senza firma valida il server ricontrolla da se': il pannello non decide mai.

export interface ImpresaRegistro {
  denominazione: string | null;
  stato: string | null;
  ateco: string | null;
  ateco_descrizione: string | null;
  pec: string | null;
  forma: string | null;
}

export interface VerificaImpresa {
  /** Normalizzata: 11 cifre. */
  partita_iva: string;
  esito: "valida" | "sconosciuta" | "non_valida" | null;
  verdetto: "verde" | "giallo" | "rosso";
  motivo: string;
  impresa: ImpresaRegistro | null;
  /** ISO: quando il server l'ha calcolata. */
  controllata_il: string;
}

export interface VerificaFirmata extends VerificaImpresa {
  firma: string;
}

export const VALIDITA_VERIFICA_MS = 60 * 60 * 1000;

function testoONull(x: unknown): string | null {
  return typeof x === "string" && x.trim() ? x.trim() : null;
}

/** La forma esatta che si firma: campi noti, in ordine fisso, vuoti = null. */
function verificaPulita(v: unknown): VerificaImpresa | null {
  const o = (v ?? {}) as Record<string, unknown>;
  const verdetto = o.verdetto;
  if (verdetto !== "verde" && verdetto !== "giallo" && verdetto !== "rosso") return null;
  const esito = o.esito === "valida" || o.esito === "sconosciuta" || o.esito === "non_valida" ? o.esito : null;
  const piva = normalizzaPartitaIva(String(o.partita_iva ?? ""));
  const quando = testoONull(o.controllata_il);
  if (piva.length !== 11 || !quando) return null;
  const i = o.impresa && typeof o.impresa === "object" ? (o.impresa as Record<string, unknown>) : null;
  return {
    partita_iva: piva,
    esito,
    verdetto,
    motivo: String(o.motivo ?? "").slice(0, 300),
    impresa: i
      ? {
        denominazione: testoONull(i.denominazione),
        stato: testoONull(i.stato),
        ateco: testoONull(i.ateco),
        ateco_descrizione: testoONull(i.ateco_descrizione),
        pec: testoONull(i.pec),
        forma: testoONull(i.forma),
      }
      : null,
    controllata_il: quando,
  };
}

/**
 * La risposta della prova a secco di verifica-piva, nella forma che si firma.
 * null = la risposta non ha un verdetto (funzione cambiata o andata male).
 */
export function verificaDaRisposta(partitaIva: string, risposta: unknown, adessoIso: string): VerificaImpresa | null {
  const r = (risposta ?? {}) as Record<string, unknown>;
  return verificaPulita({ ...r, partita_iva: partitaIva, controllata_il: adessoIso });
}

function testoDaFirmare(v: VerificaImpresa): string {
  const i = v.impresa;
  return JSON.stringify([
    v.partita_iva, v.esito, v.verdetto, v.motivo,
    i ? [i.denominazione, i.stato, i.ateco, i.ateco_descrizione, i.pec, i.forma] : null,
    v.controllata_il,
  ]);
}

async function hmacEsadecimale(segreto: string, testo: string): Promise<string> {
  const codifica = new TextEncoder();
  const chiave = await crypto.subtle.importKey("raw", codifica.encode(segreto), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  const firma = await crypto.subtle.sign("HMAC", chiave, codifica.encode(testo));
  return Array.from(new Uint8Array(firma), (b) => b.toString(16).padStart(2, "0")).join("");
}

export async function firmaVerifica(segreto: string, v: VerificaImpresa): Promise<VerificaFirmata> {
  const pulita = verificaPulita(v);
  if (!pulita) throw new Error("verifica non firmabile");
  return { ...pulita, firma: await hmacEsadecimale(segreto, testoDaFirmare(pulita)) };
}

/**
 * La verifica rimandata dal pannello e' quella che il server ha firmato, per
 * QUESTA partita IVA, da meno di un'ora? Se si', la restituisce pulita;
 * altrimenti null (e il server ricontrolla da se').
 */
export async function verificaFirmataValida(
  segreto: string,
  v: unknown,
  partitaIva: string,
  adesso: number = Date.now(),
): Promise<VerificaImpresa | null> {
  const pulita = verificaPulita(v);
  const firma = (v as { firma?: unknown } | null)?.firma;
  if (!pulita || typeof firma !== "string") return null;
  if (pulita.partita_iva !== normalizzaPartitaIva(partitaIva)) return null;
  const quando = Date.parse(pulita.controllata_il);
  if (!Number.isFinite(quando) || adesso - quando > VALIDITA_VERIFICA_MS || quando - adesso > 60_000) return null;
  const attesa = await hmacEsadecimale(segreto, testoDaFirmare(pulita));
  if (attesa.length !== firma.length) return null;
  let diverso = 0;
  for (let k = 0; k < attesa.length; k++) diverso |= attesa.charCodeAt(k) ^ firma.charCodeAt(k);
  return diverso === 0 ? pulita : null;
}

/**
 * Cosa scrivere nel profilo appena creato: le stesse colonne e la stessa
 * regola di verifica-piva. Se nessun registro ha risposto (esito null e
 * nessuna impresa) si scrive solo il verdetto «giallo» col motivo: l'esito
 * resta vuoto e la chat rifa' il controllo alla prima domanda.
 */
export function aggiornamentoDaVerifica(v: VerificaImpresa): Record<string, unknown> {
  const motivo = `${v.motivo} (controllata all'iscrizione dal pannello)`.trim();
  if (v.esito === null && !v.impresa) {
    return { impresa_verdetto: v.verdetto, impresa_motivo: motivo, impresa_controllata_il: v.controllata_il };
  }
  const agg: Record<string, unknown> = {
    piva_esito: v.esito,
    piva_controllata_il: v.controllata_il,
    impresa_verdetto: v.verdetto,
    impresa_motivo: motivo,
    impresa_controllata_il: v.controllata_il,
  };
  if (v.impresa) {
    Object.assign(agg, {
      impresa_denominazione: v.impresa.denominazione,
      impresa_stato: v.impresa.stato,
      impresa_ateco: v.impresa.ateco,
      impresa_ateco_descrizione: v.impresa.ateco_descrizione,
      impresa_pec: v.impresa.pec,
      impresa_forma: v.impresa.forma,
      impresa_dati: null,
    });
  }
  return agg;
}

/** Rossa = impresa cessata/sospesa, sconosciuta al Registro, o numero impossibile: non si iscrive. */
export function verificaBloccante(v: VerificaImpresa | null): boolean {
  return !!v && (v.verdetto === "rosso" || v.esito === "non_valida");
}

/** Tetto di richieste in memoria (per istanza della funzione), come in send-auth-email. */
export function creaTetto(finestraMs: number, massimo: number) {
  const mappa = new Map<string, number[]>();
  return (chiave: string, adesso = Date.now()): boolean => {
    const recenti = (mappa.get(chiave) ?? []).filter((t) => adesso - t < finestraMs);
    if (recenti.length >= massimo) {
      mappa.set(chiave, recenti);
      return true;
    }
    recenti.push(adesso);
    mappa.set(chiave, recenti);
    return false;
  };
}
